import React, { useState, useRef, useEffect } from 'react';
import type { SelectChangeEvent } from '@mui/material';
import { isIOS } from '../../../utils/deviceUtils';
import { createAudioRecorder, type OnErrorRef } from '../createAudioRecorder';
import type { AudioRecording, ErrorState } from '../types';

export interface UseRoleplayAudioParams {
  onError: (err: ErrorState) => void;
}

export interface UseRoleplayAudioResult {
  audioRecorder: React.MutableRefObject<AudioRecording>;
  isAudioInitialized: boolean;
  audioDevices: MediaDeviceInfo[];
  selectedAudioDevice: string;
  setSelectedAudioDevice: (id: string) => void;
  showMicDialog: boolean;
  setShowMicDialog: (show: boolean) => void;
  handleMicChange: (event: SelectChangeEvent<string>) => void;
  openMicDialog: () => Promise<void>;
  initializeWithSelectedMic: (deviceId: string) => Promise<void>;
}

export function useRoleplayAudio(params: UseRoleplayAudioParams): UseRoleplayAudioResult {
  const { onError } = params;
  const onErrorRef = useRef<((err: ErrorState) => void) | undefined>(undefined);
  onErrorRef.current = onError;

  const audioRecorder = useRef<AudioRecording>(createAudioRecorder(onErrorRef as OnErrorRef));

  const [isAudioInitialized, setIsAudioInitialized] = useState(false);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState('');
  const [showMicDialog, setShowMicDialog] = useState(false);

  const getAudioDevices = async (): Promise<MediaDeviceInfo[]> => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputDevices = devices.filter((d) => d.kind === 'audioinput');
      setAudioDevices(audioInputDevices);
      if (audioInputDevices.length > 0 && !selectedAudioDevice) {
        setSelectedAudioDevice(audioInputDevices[0].deviceId);
      }
      return audioInputDevices;
    } catch (error) {
      console.error('マイクデバイスの取得に失敗しました:', error);
      const message = error instanceof Error ? error.message : '不明なエラー';
      onError({ type: 'general', message: `マイクデバイスの取得に失敗しました: ${message}` });
      return [];
    }
  };

  const initializeWithSelectedMic = async (deviceId: string) => {
    console.log('Initializing microphone with device:', deviceId, 'useWebRTC:', audioRecorder.current.useWebRTC);
    setIsAudioInitialized(false);
    onError(null);
    try {
      audioRecorder.current.cleanup();
      const preferredMimeTypes = isIOS()
        ? [
            'audio/wav',
            'audio/mp4',
            'audio/aac',
            'audio/mp4;codecs=mp4a.40.2',
            'audio/webm;codecs=opus',
            'audio/webm',
            'audio/ogg',
          ]
        : [
            'audio/webm;codecs=opus',
            'audio/ogg;codecs=opus',
            'audio/webm',
            'audio/mp4;codecs=mp4a.40.2',
            'audio/aac',
            'audio/ogg',
          ];
      await audioRecorder.current.initialize(preferredMimeTypes, deviceId);
      console.log('マイク初期化完了:', {
        deviceId: deviceId || 'default',
        mimeType: audioRecorder.current.mimeType,
        platform: isIOS() ? 'iOS' : 'other',
        useWebRTC: audioRecorder.current.useWebRTC,
      });
      setIsAudioInitialized(true);
    } catch (error) {
      console.error('マイクの初期化に失敗:', error);
      let errorMessage = 'マイク初期化失敗: ';
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          errorMessage += 'マイクのアクセス許可が拒否されました。ブラウザ設定でマイクアクセスを許可してください。';
        } else if (error.name === 'NotFoundError') {
          errorMessage += '指定されたマイクデバイスが見つかりません。';
        } else if (error.name === 'NotReadableError') {
          errorMessage += 'マイクデバイスが他のアプリケーションで使用中の可能性があります。';
        } else if (error.message.includes('timeout')) {
          errorMessage += 'マイクの初期化がタイムアウトしました。デバイスを確認してください。';
        } else {
          errorMessage += error.message;
        }
      } else {
        errorMessage += '不明なエラー';
      }
      onError({ type: 'audio', message: errorMessage });
      setIsAudioInitialized(false);
      audioRecorder.current.cleanup();
    }
  };

  const handleMicChange = (event: SelectChangeEvent<string>) => {
    const newDeviceId = event.target.value;
    setSelectedAudioDevice(newDeviceId);
    initializeWithSelectedMic(newDeviceId);
  };

  const openMicDialog = async () => {
    await getAudioDevices();
    setShowMicDialog(true);
  };

  useEffect(() => {
    const initAudio = async () => {
      const devices = await getAudioDevices();
      if (devices.length > 0) {
        await initializeWithSelectedMic(devices[0].deviceId);
      } else {
        try {
          const preferredMimeTypes = [
            'audio/webm;codecs=opus',
            'audio/ogg;codecs=opus',
            'audio/webm',
            'audio/mp4',
            'audio/aac',
            'audio/ogg',
          ];
          await audioRecorder.current.initialize(preferredMimeTypes, '');
          setIsAudioInitialized(true);
        } catch (error) {
          console.error('Audio recorder initialization failed:', error);
          const message = error instanceof Error ? error.message : '不明なエラー';
          onError({
            type: 'audio',
            message: `マイク初期化失敗: ${message}. ブラウザ設定でマイクアクセスが許可されているか確認してください。`,
          });
          setIsAudioInitialized(false);
        }
      }
    };
    initAudio();
    return () => {
      audioRecorder.current.cleanup();
    };
  }, []);

  return {
    audioRecorder,
    isAudioInitialized,
    audioDevices,
    selectedAudioDevice,
    setSelectedAudioDevice,
    showMicDialog,
    setShowMicDialog,
    handleMicChange,
    openMicDialog,
    initializeWithSelectedMic,
  };
}
